
import { users } from "../data-mock/InitData";
import UserRepository from "../domain/data/UserRepository";
import User from "../domain/entities/User";
import AwsApiClient from "../presentation/hooks/AwsApiClient";

export default class APIUserRepository implements UserRepository {
  private client = new AwsApiClient();
  private static users: User[] = users;

  async saveUser(user: User, userObject: User): Promise<User> {
    try {
      const response = await this.client.post(
        `/api/user/${user.organization.id}`,
        userObject
      );
      return response;
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  async cognitoAuthLogin(username: string, password: string): Promise<any> {
    try {
      const response = await this.client.postExternal(
        `/api/cognito/login`,
        { username, password }
      );
      if (response.challenge === "NEW_PASSWORD_REQUIRED") {
        return {
          challenge: "NEW_PASSWORD_REQUIRED",
          username: response.username,
        };
      }
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }

  async cognitoNewPasswordLogin(username: string, tempPassword: string, newPassword: string): Promise<any> {
    try {
      const response = await this.client.postExternal(
        `/api/cognito/newPasswordLogin`,
        { username, tempPassword, newPassword }
      );
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }

  async cognitoCheckUserExist(email: string): Promise<boolean> {
    const jsonParams = new URLSearchParams();
    jsonParams.append("email", email);
    try {
      console.log("Checking user existence for:", email);
      const response = await this.client.post(
        `/api/cognito/checkuserexists`,
        jsonParams,
        "json",
        "application/x-www-form-urlencoded"
      ); 
      console.log("Checking user existence for2:", response);
      // Ensure the response is a boolean
      if (typeof response === "string") {
        return response === "true";
      }
      if (typeof response === "number") {
        return response === 1;
      }
      return Boolean(response);
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async cognitoResendUser(
    userid: string,
    organizationId: string,
    email: string
  ): Promise<boolean> {
    // The temporary password is generated server-side; it is never sent
    // from the client.
    const jsonParams = new URLSearchParams();
    jsonParams.append("userid", userid);
    jsonParams.append("organizationId", organizationId);
    jsonParams.append("email", email);
    try {
      const response = await this.client.post(
        `/api/cognito/resenduser`,
        jsonParams,
        "json",
        "application/x-www-form-urlencoded"
      );
      if (response?.success === false) {
        throw new Error(response.message || "Failed to resend user details");
      }
      return true;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async cognitoCreateUser(
    userid: string,
    organizationId: string,
    email: string
  ): Promise<boolean> {
    // The temporary password is generated server-side; it is never sent
    // from the client.
    const jsonParams = new URLSearchParams();
    jsonParams.append("userid", userid);
    jsonParams.append("organizationId", organizationId);
    jsonParams.append("email", email);
    try {
      const response = await this.client.post(
        `/api/cognito/user`,
        jsonParams,
        "none",
        "application/x-www-form-urlencoded"
      );
      return true;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }


  async deleteUser(user: User, userObject: User): Promise<boolean> {
    try {
      const response = await this.client.delete(
        `/api/user/${user.organization.id}/${userObject.id}/${userObject.email}`,
        "none"
      );
      return true;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getRequestors(user: User): Promise<User[]> {
    throw new Error("Method not implemented.");
  }

  async loadSignerDataByShortId(shortid: string): Promise<string> {
    try {
      const response = await this.client.get(`/api/sign/${shortid}`);
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getUserByShortId(shortId: string): Promise<User | null> {
    try {
      const response = await this.client.get(`/api/ext/user/email/${shortId}`);
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getUserByEmail(
    organizationId: string,
    email: string
  ): Promise<User | null> {
    try {
      const response = await this.client.get(
        `/api/user/email/${organizationId}/${email}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getUsers(user: User): Promise<User[]> {
    try {
      const response = await this.client.get(
        `/api/users/${user.organization.id}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async registerUser(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    orgName: string,
    phoneNumber?: string,
    smsConsent?: boolean
  ): Promise<User | undefined> {
    try {
      const response = await this.client.postExternal(
        "/api/user/signup",
        {
          firstName,
          lastName,
          email,
          password,
          orgName,
          phoneNumber: phoneNumber || "",
          smsConsent: smsConsent ? "true" : "false",
        }
      );
      return response;
    } catch (error: any) {
      const rawMessage = error?.message || "";
      const match = rawMessage.match(/- (.+)$/);
      const backendMessage = match?.[1]?.trim() || "Unexpected error";

      throw backendMessage;
    }
  }

  async login(username: string, password: string): Promise<User[] | undefined> {
    return APIUserRepository.users.filter((user) => user.username === username);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.client.post(
      `/api/cognito/change-password`,
      { currentPassword, newPassword },
      "none"
    );
  }

  async requestPasswordUpdate(email: string): Promise<boolean> {
    try {
      const response = await this.client.postExternal(
        "/api/cognito/request-password-reset", { email }
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async passwordUpdate(
    email: string,
    password: string,
    resetCode: string
  ): Promise<boolean> {
    try {
      const response = await this.client.postExternal(
        "/api/cognito/confirm-password-reset",
        {
          email,
          code: resetCode,
          newPassword: password,
        }
      );
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }

  async cognitoDecode(tokenUrlencoded: URLSearchParams): Promise<User[]> {
    try {
      const response = await this.client.post(
        `/api/cognito/decode`,
        tokenUrlencoded,
        "json",
        "application/x-www-form-urlencoded"
      );
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }

  async cognitoVerify(codeUrlencoded: URLSearchParams): Promise<any> {
    try {
      const response = await this.client.post(
        `/api/cognito/verify`,
        codeUrlencoded,
        "json",
        "application/x-www-form-urlencoded"
      );
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }

  async mfaRespond(username: string, session: string, code: string): Promise<any> {
    try {
      return await this.client.postExternal(`/api/cognito/mfa/respond`, { username, session, code });
    } catch (error: any) {
      const msg = error?.message || "";
      if (msg.includes("MFA_SESSION_EXPIRED")) {
        throw new Error("MFA_SESSION_EXPIRED");
      }
      throw new Error("MFA_INVALID_CODE");
    }
  }

  async mfaSetup(accessToken: string): Promise<{ secretCode: string; email: string }> {
    try {
      return await this.client.post(`/api/cognito/mfa/setup`, { accessToken });
    } catch (error) {
      throw new Error(`MFA setup failed: ${error}`);
    }
  }

  async mfaVerifySetup(accessToken: string, code: string): Promise<{ status: string }> {
    try {
      return await this.client.post(`/api/cognito/mfa/verify-setup`, { accessToken, code });
    } catch (error) {
      throw new Error(`MFA verify setup failed: ${error}`);
    }
  }
}
